/**
 * Authentication Context
 * Provides auth state and methods throughout the application
 */

import React, { createContext, useState, useEffect, useCallback } from 'react'
import supabase from '../utils/supabaseClient'
import logger from '../utils/logger'

// Create the auth context
export const AuthContext = createContext(null)

/**
 * AuthProvider component
 * Wraps the app to provide authentication state globally
 */
export function AuthProvider({ children }) {
    const [user, setUser] = useState(null)
    const [profile, setProfile] = useState(null)
    const [loading, setLoading] = useState(false)
    const [initializing, setInitializing] = useState(true)
    const [session, setSession] = useState(null)

    /**
     * Fetch user profile from the profiles table
     */
    const fetchProfile = useCallback(async (userId) => {
        try {
            const { data, error } = await supabase
                .from('profiles')
                .select('*')
                .eq('id', userId)
                .single()

            if (error) {
                logger.warn('Failed to fetch profile', { userId, error: error.message })
                return null
            }

            return data
        } catch (error) {
            logger.error('Error fetching profile', { error: error.message })
            return null
        }
    }, [])

    /**
     * Initialize auth state on mount
     */
    useEffect(() => {
        // Get initial session
        const initializeAuth = async () => {
            try {
                const { data: { session: currentSession } } = await supabase.auth.getSession()

                if (currentSession) {
                    setSession(currentSession)
                    setUser(currentSession.user)

                    // Fetch profile
                    const userProfile = await fetchProfile(currentSession.user.id)
                    setProfile(userProfile)

                    logger.info('Auth initialized with existing session', {
                        userId: currentSession.user.id
                    })
                }
            } catch (error) {
                logger.error('Error initializing auth', { error: error.message })
            } finally {
                setInitializing(false)
            }
        }

        initializeAuth()

        // Listen for auth state changes
        const { data: { subscription } } = supabase.auth.onAuthStateChange(
            (event, newSession) => {
                logger.info('Auth state changed', { event })

                if (newSession) {
                    setSession(newSession)
                    setUser(newSession.user)

                    // Fetch profile on sign in or token refresh
                    // IMPORTANT: Use setTimeout to defer Supabase calls to avoid deadlock
                    // Making Supabase calls directly inside onAuthStateChange causes signOut to hang
                    if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
                        setTimeout(async () => {
                            const userProfile = await fetchProfile(newSession.user.id)
                            setProfile(userProfile)
                        }, 0)
                    }
                } else {
                    setSession(null)
                    setUser(null)
                    setProfile(null)
                }
            }
        )

        return () => {
            subscription.unsubscribe()
        }
    }, [fetchProfile])

    /**
     * Sign up with email and password
     */
    const signUp = async (email, password, displayName = null) => {
        setLoading(true)

        try {
            const options = displayName
                ? { data: { display_name: displayName } }
                : {}

            const { data, error } = await supabase.auth.signUp({
                email,
                password,
                options
            })

            if (error) {
                logger.warn('Sign up failed', { error: error.message })
                return { error }
            }

            logger.info('Sign up successful', { userId: data.user?.id })
            return { data }
        } catch (error) {
            logger.error('Sign up error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Sign in with email and password
     */
    const signIn = async (email, password) => {
        setLoading(true)

        try {
            const { data, error } = await supabase.auth.signInWithPassword({
                email,
                password
            })

            if (error) {
                logger.warn('Sign in failed', { error: error.message })
                return { error }
            }

            logger.info('Sign in successful', { userId: data.user?.id })
            return { data }
        } catch (error) {
            logger.error('Sign in error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Sign in with OAuth provider (for future social login)
     */
    const signInWithProvider = async (provider) => {
        try {
            const { data, error } = await supabase.auth.signInWithOAuth({
                provider,
                options: {
                    redirectTo: window.location.origin
                }
            })

            if (error) {
                logger.warn('OAuth sign in failed', { provider, error: error.message })
                return { error }
            }

            return { data }
        } catch (error) {
            logger.error('OAuth sign in error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        }
    }

    /**
     * Sign in with Phone (OTP)
     * @param {string} phone - Phone number with country code
     */
    const signInWithPhone = async (phone) => {
        setLoading(true)
        try {
            const { error } = await supabase.auth.signInWithOtp({
                phone,
                options: {
                    channel: 'sms'
                }
            })

            if (error) {
                logger.warn('Phone sign in failed', { error: error.message })
                return { error }
            }

            logger.info('Phone OTP sent', { phone })
            return { success: true }
        } catch (error) {
            logger.error('Phone sign in error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Verify Phone OTP
     * @param {string} phone - Phone number
     * @param {string} token - OTP code
     */
    const verifyPhoneOtp = async (phone, token) => {
        setLoading(true)
        try {
            const { data, error } = await supabase.auth.verifyOtp({
                phone,
                token,
                type: 'sms'
            })

            if (error) {
                logger.warn('Phone verification failed', { error: error.message })
                return { error }
            }

            logger.info('Phone verified and logged in', { userId: data.user?.id })
            return { data }
        } catch (error) {
            logger.error('Phone verification error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Sign out
     * Note: If session was already invalidated (e.g., from another browser),
     * we still clear local state to ensure consistent UX
     */
    const signOut = async () => {
        try {
            const { error } = await supabase.auth.signOut()

            if (error) {
                // Log the error but still clear local state
                // This handles the case where session was already invalidated from another browser
                logger.warn('Sign out warning', { error: error.message })
            } else {
                logger.info('Sign out successful')
            }

            // Always clear local state regardless of server response
            setUser(null)
            setProfile(null)
            setSession(null)
            return {}
        } catch (error) {
            logger.error('Sign out error', { error: error.message })
            // Still clear local state to ensure user appears signed out
            setUser(null)
            setProfile(null)
            setSession(null)
            return { error: { message: 'An unexpected error occurred' } }
        }
    }

    /**
     * Update user profile
     */
    const updateProfile = async (updates) => {
        if (!user) {
            return { error: { message: 'Not authenticated' } }
        }

        try {
            const { data, error } = await supabase
                .from('profiles')
                .update(updates)
                .eq('id', user.id)
                .select()
                .single()

            if (error) {
                logger.warn('Profile update failed', { error: error.message })
                return { error }
            }

            setProfile(data)
            logger.info('Profile updated', { userId: user.id })
            return { data }
        } catch (error) {
            logger.error('Profile update error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        }
    }

    /**
     * Get the current access token for API calls
     */
    const getAccessToken = () => {
        return session?.access_token || null
    }

    /**
     * Request password reset email
     * @param {string} email - User's email address
     */
    const resetPassword = async (email) => {
        setLoading(true)

        try {
            const { error } = await supabase.auth.resetPasswordForEmail(email, {
                redirectTo: `${window.location.origin}/reset-password`
            })

            if (error) {
                logger.warn('Password reset request failed', { error: error.message })
                return { error }
            }

            logger.info('Password reset email sent', { email })
            return { success: true }
        } catch (error) {
            logger.error('Password reset error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Update password after reset
     * @param {string} newPassword - New password to set
     */
    const updatePassword = async (newPassword) => {
        setLoading(true)

        try {
            const { data, error } = await supabase.auth.updateUser({
                password: newPassword
            })

            if (error) {
                logger.warn('Password update failed', { error: error.message })
                return { error }
            }

            logger.info('Password updated successfully')
            return { data }
        } catch (error) {
            logger.error('Password update error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        } finally {
            setLoading(false)
        }
    }

    /**
     * Sign in with magic link (passwordless)
     * @param {string} email - User's email address
     */
    const signInWithMagicLink = async (email) => {
        setLoading(true)

        try {
            const { error } = await supabase.auth.signInWithOtp({
                email,
                options: {
                    emailRedirectTo: `${window.location.origin}/`
                }
            })

            if (error) {
                logger.warn('Magic link request failed', { error: error.message })
                return { error }
            }

            logger.info('Magic link email sent', { email })
            return { success: true }
        } catch (error) {
            logger.error('Magic link error', { error: error.message })
            return { error: { message: 'An unexpected error occurred' } }
        } finally {
            setLoading(false)
        }
    }

    const value = {
        user,
        profile,
        loading: loading || initializing,
        initializing,
        session,
        isAuthenticated: !!user,
        isAdmin: profile?.role === 'admin',
        signUp,
        signIn,
        signInWithProvider,
        signInWithMagicLink,
        signOut,
        updateProfile,
        getAccessToken,
        resetPassword,
        updatePassword,
        signInWithPhone,
        verifyPhoneOtp,
        refreshProfile: () => user && fetchProfile(user.id).then(setProfile)
    }

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    )
}

/**
 * Custom hook to use auth context
 */
export function useAuth() {
    const context = React.useContext(AuthContext)
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider')
    }
    return context
}

export default AuthContext
